import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { generateErrorMessage } from "app/utils/GenerateMessage";
import { GetComplienceReport } from "app/services/Services";
import { convertedDate, attendanceConvertedDate } from "app/utils/utils";
import moment from "moment";
import { COLORS, FONTS, RADII, getInitials, TINTS } from "app/views/dashboard/shared/tokens";
import { TABLE_CARD_SX, COMPLIANCE_COLORS } from "../AdminList";

/* ─── Grade derivation ─── */
function deriveGrade(presentDays, workingDays) {
  if (!workingDays || workingDays === 0) return { grade: "N/A", key: "actionneeded", pct: 0 };
  const ratio = (presentDays / workingDays) * 100;
  if (ratio >= 95) return { grade: "Compliant", key: "compliant", pct: ratio };
  if (ratio >= 85) return { grade: "At risk", key: "atrisk", pct: ratio };
  return { grade: "Action needed", key: "actionneeded", pct: ratio };
}

const GRADE_STYLES = {
  compliant:    { color: "#2f7d5a", bg: "#eaf8f0", border: "#cceedb", bar: "#2fbd77" },
  atrisk:       { color: "#9b5a06", bg: "#fdf3e5", border: "#f6e2c1", bar: "#e0a34a" },
  actionneeded: { color: "#c23b50", bg: "#fdf1f3", border: "#f7d8de", bar: "#e05468" },
};

const HEAD_CELL = {
  fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: COLORS.muted, userSelect: "none",
};
const GRID_COLS = "2.3fr 1fr 0.9fr 1.2fr 1fr 1.8fr";

function ComplienceReport({
  activeDate,
  activeFilters,
  onStatCounts,
  onTotalCount,
  onClearFilters,
  exportRef,
}) {
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const dateParam = moment(activeDate).subtract(1, "days").format("YYYY-MM-DD");

  /* Debounced search */
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchText]);

  /* Fetch data */
  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {
      date: dateParam,
      page: `${page + 1}`,
      limit: rowsPerPage,
      camplaince: "",
      name: debouncedSearch || null,
    };
    GetComplienceReport(params)
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          const rows = res?.data?.data?.employeeAttendanceList || [];
          setResults(rows);
          const count = res?.data?.data?.totalList ?? 0;
          setTotalCount(count);
          onTotalCount(count);
        }
      })
      .catch((error) => {
        setLoading(false);
        generateErrorMessage(error?.response?.data?.message || "Something went wrong");
      });
  }, [page, rowsPerPage, debouncedSearch, dateParam, onTotalCount]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const { hostname } = window.location;
    document.title = `${hostname} | Compliance`;
  }, []);

  /* ── Process rows: aggregate per employee ──
     The compliance API returns per-day records. We aggregate them by employee
     to build the compliance table (present days, late count, open shifts, avg hours, grade).
     NOTE: Since the server doesn't provide pre-aggregated compliance data,
     we compute from the raw per-day records available on the current page.
     This is a client-side approximation — server-side aggregation is needed for full accuracy.
  */
  const aggregatedRows = useMemo(() => {
    const byEmp = {};
    results.forEach((row) => {
      const key = row.empId || row.employeeName;
      if (!byEmp[key]) {
        byEmp[key] = {
          empId: row.empId,
          employeeName: row.employeeName,
          presentDays: 0,
          workingDays: 0,
          lateCount: 0,
          openShifts: 0,
          totalHours: 0,
          daysCounted: 0,
        };
      }
      const emp = byEmp[key];
      emp.workingDays++;
      const status = row.leaveStatus?.toLowerCase();
      if (status === "present" || status === "complete" || row.checkInTime) {
        emp.presentDays++;
      }
      if (row.checkInTime && !row.checkOutTime) {
        emp.openShifts++;
      }
      // Simple late detection: if checkInTime exists and is after 09:15
      if (row.checkInTime) {
        let inH, inM;
        if (row.checkInTime.match(/AM|PM/i)) {
          const d = new Date(`1/1/2000 ${row.checkInTime}`);
          inH = d.getHours(); inM = d.getMinutes();
        } else {
          const parts = row.checkInTime.split(":");
          inH = parseInt(parts[0], 10); inM = parseInt(parts[1], 10);
        }
        if (inH * 60 + inM > 9 * 60 + 15) emp.lateCount++;
      }
      const hrs = parseFloat(row.totalWorkHours) || 0;
      if (hrs > 0) {
        emp.totalHours += hrs;
        emp.daysCounted++;
      }
    });
    return Object.values(byEmp).map((emp) => ({
      ...emp,
      avgHours: emp.daysCounted > 0 ? emp.totalHours / emp.daysCounted : 0,
      ...deriveGrade(emp.presentDays, emp.workingDays),
    }));
  }, [results]);

  /* Stat counts */
  useEffect(() => {
    const counts = { compliant: 0, atrisk: 0, actionneeded: 0, openshifts: 0, avghours: 0 };
    let totalHrs = 0;
    let hrsCount = 0;
    aggregatedRows.forEach((r) => {
      if (r.key === "compliant") counts.compliant++;
      else if (r.key === "atrisk") counts.atrisk++;
      else counts.actionneeded++;
      counts.openshifts += r.openShifts;
      if (r.avgHours > 0) { totalHrs += r.avgHours; hrsCount++; }
    });
    counts.avghours = hrsCount > 0 ? totalHrs / hrsCount : 0;
    onStatCounts(counts);
  }, [aggregatedRows, onStatCounts]);

  /* Filter by active stat filter */
  const displayRows = useMemo(() => {
    if (!activeFilters.length) return aggregatedRows;
    return aggregatedRows.filter((r) => {
      if (activeFilters.includes("compliant") && r.key === "compliant") return true;
      if (activeFilters.includes("atrisk") && r.key === "atrisk") return true;
      if (activeFilters.includes("actionneeded") && r.key === "actionneeded") return true;
      if (activeFilters.includes("openshifts") && r.openShifts > 0) return true;
      return false;
    });
  }, [aggregatedRows, activeFilters]);

  /* Export */
  const handleExport = useCallback(() => {
    setLoading(true);
    GetComplienceReport({ export: true, date: dateParam, camplaince: "", name: null })
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          const rows = res?.data?.data?.employeeAttendanceList || [];
          const exportData = rows.map(({ empId, employeeName, attendanceDate, leaveDate, checkInTime, checkOutTime, totalWorkHours, workMode, leaveStatus, approved }) => ({
            Id: empId,
            Name: employeeName,
            Date: convertedDate(attendanceDate) || convertedDate(leaveDate),
            Check_In_Time: checkInTime,
            Check_Out_Time: checkOutTime,
            Work_Mode: workMode,
            Total_Hours: totalWorkHours,
            Exception_Approved: approved ? "Approved" : "_",
            Status: leaveStatus,
          }));
          const ws = XLSX.utils.json_to_sheet(exportData);
          const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
          const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
          const fileName = `Compliance Report_${moment(activeDate).format("MM-YYYY")}`;
          FileSaver.saveAs(
            new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }),
            `${fileName}.xlsx`
          );
        }
      })
      .catch((error) => {
        setLoading(false);
        generateErrorMessage(error?.response?.data?.message || "Export failed");
      });
  }, [dateParam, activeDate]);

  useEffect(() => {
    if (exportRef) exportRef.current = handleExport;
  }, [exportRef, handleExport]);

  /* Pagination */
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const pageStart = page * rowsPerPage + 1;
  const pageEnd = Math.min((page + 1) * rowsPerPage, totalCount);

  return (
    <Box sx={TABLE_CARD_SX}>
      {/* FILTER BAR */}
      <Box sx={{
        p: "16px 18px", borderBottom: `1px solid ${COLORS.hairline}`,
        display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap",
      }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: "9px",
          background: "#faf8fd", border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: RADII.control, p: "9px 13px", minWidth: 250, flex: 1, maxWidth: 330,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a49bb8" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" />
          </svg>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search name or employee ID\u2026"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "'DM Sans',sans-serif", fontSize: "14px", color: "#17131f", width: "100%" }}
          />
        </Box>
        {activeFilters.length > 0 && (
          <Box component="span" onClick={onClearFilters} sx={{ fontSize: "13px", fontWeight: 600, color: "#a411c1", cursor: "pointer", p: "9px 6px" }}>Clear</Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box component="span" sx={{ fontSize: "13px", color: COLORS.muted }}>
          {totalCount > 0 ? `${pageStart}\u2013${pageEnd} of ${totalCount}` : ""}
        </Box>
      </Box>

      {/* TABLE */}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 1040 }}>
          {/* HEAD */}
          <Box sx={{
            display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center",
            gap: "14px", p: "12px 18px", borderBottom: `1px solid ${COLORS.hairline}`, background: "#fcfbfe",
          }}>
            {["Employee", "Present days", "Late count", "Open shifts", "Avg hours", "Compliance"].map((h) => (
              <Box key={h} component="span" sx={HEAD_CELL}>{h}</Box>
            ))}
          </Box>

          {/* LOADING */}
          {loading && !results.length && (
            Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} sx={{ display: "grid", gridTemplateColumns: GRID_COLS, gap: "14px", p: "14px 18px", borderBottom: "1px solid #f7f4fb" }}>
                {Array.from({ length: 6 }).map((_, j) => <Skeleton key={j} variant="rounded" height={20} sx={{ borderRadius: "6px" }} />)}
              </Box>
            ))
          )}

          {/* ROWS */}
          {displayRows.map((row, idx) => {
            const tint = TINTS[idx % TINTS.length];
            const initials = getInitials(row.employeeName || "");
            const gs = GRADE_STYLES[row.key] || GRADE_STYLES.actionneeded;
            const pctWidth = Math.min(100, row.pct);

            return (
              <Box key={row.empId || idx} sx={{
                display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center",
                gap: "14px", p: "12px 18px", borderBottom: "1px solid #f7f4fb",
                transition: "background .12s", "&:hover": { background: "#faf8fd" },
              }}>
                {/* Employee */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: "11px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.heading, fontSize: "13px", fontWeight: 600, background: tint.bg, color: tint.color }}>{initials}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.ink3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", mb: "2px" }}>{row.employeeName || "--"}</Box>
                    <Box sx={{ fontSize: "12.5px", color: COLORS.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.empId || ""}</Box>
                  </Box>
                </Box>

                {/* Present days */}
                <Box>
                  <Box sx={{ fontFamily: FONTS.heading, fontSize: "14px", fontWeight: 600, color: COLORS.ink2 }}>{row.presentDays}</Box>
                  <Box sx={{ fontSize: "12px", color: COLORS.muted2 }}>of {row.workingDays} days</Box>
                </Box>

                {/* Late count */}
                <Box sx={{ fontFamily: FONTS.heading, fontSize: "14px", fontWeight: 600, color: row.lateCount > 3 ? COLORS.warningText : COLORS.ink2 }}>{row.lateCount}</Box>

                {/* Open shifts */}
                <Box>
                  <Box sx={{
                    display: "inline-flex", fontSize: "12.5px", fontWeight: 600, px: "10px", py: "4px", borderRadius: "7px",
                    color: row.openShifts > 0 ? "#c23b50" : COLORS.ink2,
                    background: row.openShifts > 0 ? "#fdf1f3" : "transparent",
                    border: row.openShifts > 0 ? "1px solid #f7d8de" : "none",
                  }}>
                    {row.openShifts}
                  </Box>
                  {row.openShifts > 0 && <Box sx={{ fontSize: "12px", color: COLORS.muted2, mt: "4px" }}>needs review</Box>}
                </Box>

                {/* Avg hours */}
                <Box sx={{ fontFamily: FONTS.heading, fontSize: "14px", fontWeight: 600, color: COLORS.ink2 }}>
                  {row.avgHours > 0 ? `${row.avgHours.toFixed(1)}h` : "\u2014"}
                </Box>

                {/* Compliance bar + grade */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box sx={{ flex: 1, height: 6, borderRadius: "999px", background: "#f1edf8", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", borderRadius: "999px", width: `${pctWidth}%`, background: gs.bar }} />
                  </Box>
                  <Box sx={{
                    display: "inline-flex", fontSize: "12px", fontWeight: 600,
                    color: gs.color, background: gs.bg, border: `1px solid ${gs.border}`,
                    px: "8px", py: "3px", borderRadius: "6px", whiteSpace: "nowrap",
                  }}>
                    {row.grade}
                  </Box>
                </Box>
              </Box>
            );
          })}

          {/* Empty */}
          {!loading && displayRows.length === 0 && (
            <Box sx={{ p: "66px 24px", textAlign: "center" }}>
              <Box sx={{ width: 52, height: 52, m: "0 auto 16px", borderRadius: "15px", background: "#f7f4fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b3aac4" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" /></svg>
              </Box>
              <Box sx={{ fontFamily: FONTS.heading, fontSize: "17px", fontWeight: 600, color: COLORS.ink, mb: "6px" }}>No compliance data</Box>
              <Box sx={{ fontSize: "14px", color: COLORS.muted, mb: "20px" }}>No compliance records found for this period.</Box>
              {activeFilters.length > 0 && (
                <Box component="span" onClick={onClearFilters} sx={{ display: "inline-flex", fontSize: "13.5px", fontWeight: 600, color: "#a411c1", background: "rgba(224,89,240,0.09)", border: "1px solid rgba(224,89,240,0.22)", p: "9px 17px", borderRadius: "9px", cursor: "pointer" }}>Clear all filters</Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* PAGINATION */}
      <Box sx={{ p: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" }}>
        <Box component="span" sx={{ fontSize: "13px", color: COLORS.muted }}>
          {totalCount > 0 ? `${pageStart}\u2013${pageEnd} of ${totalCount}` : "No results"}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box component="span" sx={{ fontSize: "13px", color: COLORS.muted }}>Rows</Box>
            {[12, 25, 50].map((size) => (
              <Box key={size} component="span" onClick={() => { setRowsPerPage(size); setPage(0); }} sx={{
                fontSize: "13px", fontWeight: rowsPerPage === size ? 700 : 500,
                color: rowsPerPage === size ? "#a411c1" : COLORS.muted,
                background: rowsPerPage === size ? "rgba(224,89,240,0.09)" : "transparent",
                p: "4px 9px", borderRadius: "7px", cursor: "pointer",
                border: rowsPerPage === size ? "1px solid rgba(224,89,240,0.22)" : "1px solid transparent",
              }}>{size}</Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: "6px" }}>
            <Box component="span" onClick={page > 0 ? () => setPage(page - 1) : undefined} sx={{ width: 32, height: 32, borderRadius: "9px", border: `1px solid ${COLORS.cardBorder}`, background: COLORS.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: page > 0 ? "pointer" : "not-allowed", opacity: page > 0 ? 1 : 0.4 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M14 6l-6 6 6 6" /></svg>
            </Box>
            <Box component="span" onClick={page < totalPages - 1 ? () => setPage(page + 1) : undefined} sx={{ width: 32, height: 32, borderRadius: "9px", border: `1px solid ${COLORS.cardBorder}`, background: COLORS.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: page < totalPages - 1 ? "pointer" : "not-allowed", opacity: page < totalPages - 1 ? 1 : 0.4 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M10 6l6 6-6 6" /></svg>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ComplienceReport;
